import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Edit as EditIcon, RefreshCw as RefreshIcon, Save as SaveIcon, Trash2 as DeleteIcon, UserPlus as UserPlusIcon } from 'lucide-react';
import { getAuth } from 'firebase/auth';
import { prepareVolunteerOperation, acknowledgeVolunteerOperation } from '../../services/moVolunteerOperation';
import { useEventEdition } from '../../contexts/EventEditionContext';
import {
  deleteMoVolunteer,
  enrichMoVolunteerFromPublicProfile,
  getMoVolunteersForEdition,
  MOVolunteerEntry,
  MOVolunteerPayload,
  MOVolunteerUserOption,
  searchMoVolunteerUsers,
  upsertMoVolunteer,
} from '../../services/moVolunteerService';

// Remains disabled in deployed builds until strict backend readiness/cutover.
const strictIdentity = process.env.REACT_APP_PERSON_IDENTITY_POLICY === 'strict-v1';

interface VolunteerFormState {
  createPerson: boolean;
  resultId?: string | null;
  userId?: string | null;
  personId?: number | null;
  searchText: string;
  firstName: string;
  lastName: string;
  email: string;
  yearOfBirth: string;
  gender: 'male' | 'female' | '';
  representing: string;
}

const emptyForm: VolunteerFormState = {
  createPerson: false,
  resultId: null,
  userId: null,
  personId: null,
  searchText: '',
  firstName: '',
  lastName: '',
  email: '',
  yearOfBirth: '',
  gender: '',
  representing: '',
};

const splitName = (value: string): { firstName: string; lastName: string } => {
  const parts = value.trim().split(/\s+/).filter(Boolean);
  if (parts.length <= 1) {
    return { firstName: parts[0] ?? '', lastName: '' };
  }
  return {
    firstName: parts.slice(0, -1).join(' '),
    lastName: parts[parts.length - 1],
  };
};

const normalizeRepresenting = (value: string): string[] =>
  value.split(',').map((item) => item.trim()).filter(Boolean);

const genderLabel = (gender: string | null | undefined): string => {
  if (gender === 'male') return 'Male';
  if (gender === 'female') return 'Female';
  return '—';
};

const MOVolunteerPanel: React.FC = () => {
  const { event: selectedEvent } = useEventEdition();
  const [volunteers, setVolunteers] = useState<MOVolunteerEntry[]>([]);
  const [userOptions, setUserOptions] = useState<MOVolunteerUserOption[]>([]);
  const [form, setForm] = useState<VolunteerFormState>(emptyForm);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const isMoEdition = selectedEvent?.eventId === 'mo' || selectedEvent?.id?.startsWith('mo-');
  const editionYear = useMemo(() => {
    if (typeof selectedEvent?.edition === 'number') {
      return selectedEvent.edition;
    }
    const match = selectedEvent?.id?.match(/(\d{4})/);
    return match ? Number(match[1]) : null;
  }, [selectedEvent?.edition, selectedEvent?.id]);

  const loadVolunteers = useCallback(async () => {
    if (!selectedEvent?.id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getMoVolunteersForEdition(selectedEvent.id);
      setVolunteers(data);
    } catch (err) {
      setError('Failed to load volunteers: ' + (err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [selectedEvent?.id]);

  useEffect(() => {
    loadVolunteers();
  }, [loadVolunteers]);

  useEffect(() => {
    const search = async () => {
      const lookup = form.searchText.trim();
      if (lookup.length < 2) {
        setUserOptions([]);
        return;
      }
      setSearching(true);
      try {
        const users = await searchMoVolunteerUsers(lookup);
        setUserOptions(users);
      } catch (err) {
        setError('Failed to search users: ' + (err as Error).message);
      } finally {
        setSearching(false);
      }
    };

    const timeout = window.setTimeout(search, 250);
    return () => window.clearTimeout(timeout);
  }, [form.searchText]);

  const updateForm = (patch: Partial<VolunteerFormState>) => {
    setForm((current) => ({ ...current, ...patch }));
    setSuccessMsg(null);
    setError(null);
  };

  const selectUser = async (user: MOVolunteerUserOption) => {
    const enrichedUser = await enrichMoVolunteerFromPublicProfile(user);
    updateForm({
      createPerson: false,
      userId: enrichedUser.userId || null,
      personId: enrichedUser.personId ?? null,
      searchText: enrichedUser.displayName,
      firstName: enrichedUser.firstName,
      lastName: enrichedUser.lastName,
      email: enrichedUser.email ?? '',
      yearOfBirth: enrichedUser.yearOfBirth ? String(enrichedUser.yearOfBirth) : '',
      gender: enrichedUser.gender ?? '',
      representing: enrichedUser.representing?.join(', ') ?? '',
    });
  };

  const editVolunteer = (volunteer: MOVolunteerEntry) => {
    updateForm({
      resultId: volunteer.id,
      createPerson: false,
      userId: volunteer.userId,
      personId: volunteer.personId ?? null,
      searchText: volunteer.displayName,
      firstName: volunteer.firstName,
      lastName: volunteer.lastName,
      email: volunteer.email ?? '',
      yearOfBirth: volunteer.yearOfBirth ? String(volunteer.yearOfBirth) : '',
      gender: volunteer.gender ?? '',
      representing: volunteer.representing?.join(', ') ?? '',
    });
  };

  const resetForm = () => {
    setForm(emptyForm);
    setUserOptions([]);
    setError(null);
    setSuccessMsg(null);
  };

  const saveVolunteer = async () => {
    if (!selectedEvent?.id || !editionYear) {
      setError('Select an MO event edition before saving volunteers.');
      return;
    }
    let firstName = form.firstName.trim();
    let lastName = form.lastName.trim();
    if (!firstName || !lastName) {
      const fromSearch = splitName(form.searchText);
      firstName = firstName || fromSearch.firstName;
      lastName = lastName || fromSearch.lastName;
    }
    if (!firstName || !lastName) {
      setError('First name and last name are required.');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccessMsg(null);
    try {
      const payload: MOVolunteerPayload = {
        resultId: form.resultId ?? null,
        editionId: selectedEvent.id,
        editionYear,
        userId: form.userId ?? null,
        personId: form.personId ?? null,
        firstName,
        lastName,
        displayName: `${firstName} ${lastName}`.trim(),
        email: form.email.trim() || null,
        yearOfBirth: form.yearOfBirth ? Number(form.yearOfBirth) : null,
        gender: form.gender,
        representing: normalizeRepresenting(form.representing),
      };
      const actorId = getAuth().currentUser?.uid || '';
      const reviewedPayload = strictIdentity
        ? await prepareVolunteerOperation(payload, actorId, form.createPerson) : payload;
      const result = await upsertMoVolunteer(reviewedPayload);
      if (strictIdentity) acknowledgeVolunteerOperation(reviewedPayload, actorId, result);
      resetForm();
      setSuccessMsg(form.resultId ? 'Volunteer updated.' : 'Volunteer added.');
      await loadVolunteers();
    } catch (err) {
      setError('Failed to save volunteer: ' + (err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const removeVolunteer = async (volunteer: MOVolunteerEntry) => {
    const confirmed = window.confirm(`Remove ${volunteer.displayName} as volunteer for ${selectedEvent?.eventShortName || selectedEvent?.id}?`);
    if (!confirmed) return;

    setDeletingId(volunteer.id);
    setError(null);
    setSuccessMsg(null);
    try {
      await deleteMoVolunteer(volunteer.id);
      if (form.resultId === volunteer.id) {
        resetForm();
      }
      setSuccessMsg('Volunteer removed.');
      await loadVolunteers();
    } catch (err) {
      setError('Failed to remove volunteer: ' + (err as Error).message);
    } finally {
      setDeletingId(null);
    }
  };

  if (!selectedEvent?.id) {
    return <Alert severity="info">Select an event edition to manage MO volunteers.</Alert>;
  }

  if (!isMoEdition) {
    return <Alert severity="info">Volunteer participation management is only available for Malvikingen Opp editions.</Alert>;
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>MO Volunteers</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Add, edit, or remove volunteer participation entries for the selected edition. Entries are stored in moResults as participationType "volunteer" and count in MO statistics.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {successMsg && <Alert severity="success" sx={{ mb: 2 }}>{successMsg}</Alert>}

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Stack spacing={2}>
          <Typography variant="h6">{form.resultId ? 'Edit volunteer' : 'Add volunteer'}</Typography>
          {strictIdentity && !form.userId && !form.personId && !form.resultId && (
            <FormControlLabel control={<Checkbox checked={form.createPerson}
              onChange={e => updateForm({ createPerson: e.target.checked })} disabled={saving} />}
              label="Create a new runner rather than select an existing account" />
          )}
          <TextField
            label="Search existing user by name"
            value={form.searchText}
            onChange={(e) => updateForm({ searchText: e.target.value, userId: null })}
            helperText={form.userId ? 'Existing user selected.' : 'Select a match below, or fill in name fields to create a new user profile.'}
            InputProps={{
              endAdornment: searching ? <CircularProgress size={18} /> : undefined,
            }}
          />

          {userOptions.length > 0 && (
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {userOptions.map((user) => (
                <Button key={user.userId || user.personId || user.displayName} size="small" variant={(form.userId && form.userId === user.userId) || (form.personId != null && form.personId === user.personId) ? 'contained' : 'outlined'} onClick={() => selectUser(user)}>
                  {user.displayName}{user.yearOfBirth ? ` (${user.yearOfBirth})` : ''}
                </Button>
              ))}
            </Stack>
          )}

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              label="First name"
              value={form.firstName}
              onChange={(e) => updateForm({ firstName: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Last name"
              value={form.lastName}
              onChange={(e) => updateForm({ lastName: e.target.value })}
              fullWidth
              required
            />
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              label="Email"
              value={form.email}
              onChange={(e) => updateForm({ email: e.target.value })}
              fullWidth
            />
            <TextField
              label="Year of birth"
              value={form.yearOfBirth}
              onChange={(e) => updateForm({ yearOfBirth: e.target.value.replace(/\D/g, '').slice(0, 4) })}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel id="mo-volunteer-gender-label">Gender</InputLabel>
              <Select
                labelId="mo-volunteer-gender-label"
                label="Gender"
                value={form.gender}
                onChange={(e) => updateForm({ gender: e.target.value as VolunteerFormState['gender'] })}
              >
                <MenuItem value="">Unknown</MenuItem>
                <MenuItem value="female">Female</MenuItem>
                <MenuItem value="male">Male</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          <TextField
            label="Representing / club"
            value={form.representing}
            onChange={(e) => updateForm({ representing: e.target.value })}
            helperText="Use commas for multiple clubs/teams."
            fullWidth
          />

          <Stack direction="row" spacing={1}>
            <Button variant="contained" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon size={16} />} onClick={saveVolunteer} disabled={saving}>
              {form.resultId ? 'Save changes' : 'Add volunteer'}
            </Button>
            <Button variant="outlined" onClick={resetForm} disabled={saving}>
              Clear
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="h6">Registered volunteers ({volunteers.length})</Typography>
        <Button variant="outlined" size="small" startIcon={<RefreshIcon size={16} />} onClick={loadVolunteers} disabled={loading}>
          Refresh
        </Button>
      </Stack>

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : volunteers.length === 0 ? (
        <Alert severity="info" icon={<UserPlusIcon size={18} />}>No volunteers registered for this edition yet.</Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Year of birth</TableCell>
                <TableCell>Gender</TableCell>
                <TableCell>Representing</TableCell>
                <TableCell>Person ID</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {volunteers.map((volunteer) => (
                <TableRow key={volunteer.id} hover>
                  <TableCell>{volunteer.displayName}</TableCell>
                  <TableCell>{volunteer.yearOfBirth ?? '—'}</TableCell>
                  <TableCell>{genderLabel(volunteer.gender)}</TableCell>
                  <TableCell>{volunteer.representing?.join(', ') || '—'}</TableCell>
                  <TableCell>{volunteer.personId ?? '—'}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button size="small" startIcon={<EditIcon size={14} />} onClick={() => editVolunteer(volunteer)}>
                        Edit
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        startIcon={deletingId === volunteer.id ? <CircularProgress size={14} color="inherit" /> : <DeleteIcon size={14} />}
                        onClick={() => removeVolunteer(volunteer)}
                        disabled={deletingId === volunteer.id}
                      >
                        Remove
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default MOVolunteerPanel;
